<script setup lang="ts">
import { onErrorCaptured, ref, type PropType } from 'vue'
import * as Sentry from '@sentry/vue'
import Button from 'primevue/button'

const props = defineProps({
  fallback: {
    type: String,
    default: 'An unexpected error occurred'
  },
  showDetails: {
    type: Boolean,
    default: false
  },
  onError: {
    type: Function as PropType<(error: Error) => void>,
    required: false
  }
})

const hasError = ref(false)
const error = ref<Error | null>(null)

const resetError = () => {
  hasError.value = false
  error.value = null
}

onErrorCaptured((err, instance, info) => {
  console.error('Error caught by boundary:', err)
  
  // Capture error to Sentry with additional context
  Sentry.withScope((scope) => {
    scope.setLevel('error')
    scope.setContext('errorBoundary', {
      componentInfo: info,
      componentName: instance?.$options.name || 'Unknown',
      componentProps: instance?.$props || {}
    })
    scope.addBreadcrumb({
      message: 'Component error caught',
      level: 'error',
      data: {
        error: err.message,
        stack: err.stack
      }
    })
    Sentry.captureException(err)
  })

  hasError.value = true
  error.value = err as Error
  
  // Call custom error handler if provided
  props.onError?.(err as Error)
  
  // Prevent error propagation
  return false
})
</script>

<template>
  <div v-if="hasError" class="flex flex-col items-center justify-center p-8 text-center">
    <i class="pi pi-exclamation-circle text-5xl text-red-500 mb-4"></i>
    <h2 class="text-lg font-semibold mb-2">{{ fallback }}</h2>
    <p v-if="showDetails && error" class="text-sm text-gray-600 mb-4 max-w-md">
      {{ error.message }}
    </p>
    <Button @click="resetError" severity="secondary" label="Try Again" />
  </div>
  <slot v-else />
</template>